import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate }                              from 'react-router-dom';
import { useAuth }                                  from '../contexts/AuthContext';
import {
  refMakes, refModels, refYears, refPackages, refEngines,
  createVehicle,
} from '../api/vehicles';
import './AddProjectPage.css';

const STEPS = ['Make', 'Model', 'Year', 'Package', 'Engine', 'Details'];

const BLANK = {
  make: '', model: '', year: '', pkg: '', engine: '',
  nickname: '', vin: '', transmission_desc: '', notes: '',
};

export default function AddProjectPage() {
  const { user, signOut } = useAuth();
  const navigate          = useNavigate();

  // ── Cascading options ──────────────────────────────────────────────
  const [makes,    setMakes]    = useState([]);
  const [models,   setModels]   = useState([]);
  const [years,    setYears]    = useState([]);
  const [packages, setPackages] = useState([]);
  const [engines,  setEngines]  = useState([]);

  // ── Form values ────────────────────────────────────────────────────
  const [form,    setForm]    = useState(BLANK);
  const [step,    setStep]    = useState(0);   // 0=Make…4=Engine, 5=Details
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // ── Load makes on mount ────────────────────────────────────────────
  useEffect(() => {
    refMakes()
      .then(r => setMakes(r.success ? r.data : []))
      .catch(() => setMakes([]));
  }, []);

  // ── Cascade helpers ────────────────────────────────────────────────
  const loadModels = useCallback(async (make) => {
    setModels([]); setYears([]); setPackages([]); setEngines([]);
    if (!make) return;
    const r = await refModels(make);
    setModels(r.success ? r.data : []);
  }, []);

  const loadYears = useCallback(async (make, model) => {
    setYears([]); setPackages([]); setEngines([]);
    if (!make || !model) return;
    const r = await refYears(make, model);
    setYears(r.success ? r.data : []);
  }, []);

  const loadPackages = useCallback(async (make, model, year) => {
    setPackages([]); setEngines([]);
    if (!make || !model || !year) return;
    const r = await refPackages(make, model, year);
    setPackages(r.success ? r.data : []);
  }, []);

  const loadEngines = useCallback(async (make, model, year, pkg) => {
    setEngines([]);
    if (!make || !model || !year || !pkg) return;
    const r = await refEngines(make, model, year, pkg);
    setEngines(r.success ? r.data : []);
  }, []);

  // ── Selection handlers ─────────────────────────────────────────────
  async function selectMake(make) {
    setForm(f => ({ ...f, make, model: '', year: '', pkg: '', engine: '' }));
    await loadModels(make);
    setStep(1);
  }

  async function selectModel(model) {
    setForm(f => ({ ...f, model, year: '', pkg: '', engine: '' }));
    await loadYears(form.make, model);
    setStep(2);
  }

  async function selectYear(year) {
    setForm(f => ({ ...f, year, pkg: '', engine: '' }));
    await loadPackages(form.make, form.model, year);
    setStep(3);
  }

  async function selectPackage(pkg) {
    setForm(f => ({ ...f, pkg, engine: '' }));
    await loadEngines(form.make, form.model, form.year, pkg);
    setStep(4);
  }

  function selectEngine(engine) {
    setForm(f => ({ ...f, engine }));
    setStep(5);
  }

  // ── Going back to a prior step ─────────────────────────────────────
  function goToStep(n) {
    if (n < step) setStep(n);
  }

  // ── Final save ─────────────────────────────────────────────────────
  async function handleSave() {
    if (!form.make || !form.model || !form.year) {
      setError('Make, model, and year are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        year:              parseInt(form.year),
        make:              form.make,
        model:             form.model,
        trim_package:      form.pkg             || undefined,
        engine_desc:       form.engine          || undefined,
        nickname:          form.nickname        || undefined,
        vin:               form.vin             || undefined,
        transmission_desc: form.transmission_desc || undefined,
      };

      const res = await createVehicle(payload);
      if (!res.success) throw new Error(res.message || 'Save failed');

      // Store as active vehicle
      localStorage.setItem('ar_active_vehicle', JSON.stringify({
        vehicleId:        res.data.vehicleId,
        year:             payload.year,
        make:             payload.make,
        model:            payload.model,
        trim_package:     payload.trim_package,
        engine_desc:      payload.engine_desc,
        transmission_desc: payload.transmission_desc,
        nickname:         payload.nickname,
        is_primary:       1,
      }));

      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Could not save vehicle.');
    } finally {
      setLoading(false);
    }
  }

  function handleSignOut() { signOut(); navigate('/'); }

  // ── Breadcrumb label ───────────────────────────────────────────────
  const breadcrumb = [
    form.make, form.model, form.year, form.pkg, form.engine,
  ].filter(Boolean).join(' › ');

  // ── Step button component ──────────────────────────────────────────
  const StepHeader = ({ stepIndex, label, value }) => (
    <div
      className={`ap-step-header ${step === stepIndex ? 'active' : ''} ${stepIndex < step ? 'done' : ''}`}
      onClick={() => stepIndex < step && goToStep(stepIndex)}
    >
      <span className="ap-step-num">{stepIndex + 1}</span>
      <span className="ap-step-label">{label}</span>
      {value && <span className="ap-step-value">{value}</span>}
    </div>
  );

  return (
    <div className="ap-root">

      {/* ── Header ────────────────────────────────────────────────── */}
      <header className="ap-header">
        <div className="ap-header-brand">
          <span className="ap-header-gear">⚙</span>
          <span className="ap-header-name">AutoRestoration</span>
        </div>
        <div className="ap-header-user">
          <span className="ap-username">@{user?.username}</span>
          <button className="ap-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      <div className="ap-body">

        {/* ── Page title ─────────────────────────────────────────── */}
        <div className="ap-page-header">
          <button className="ap-back-btn" onClick={() => navigate('/projects')}>
            ← My Projects
          </button>
          <h1 className="ap-page-title">Add Project Car</h1>
        </div>

        {/* ── Breadcrumb preview ─────────────────────────────────── */}
        {breadcrumb && (
          <div className="ap-breadcrumb">{breadcrumb}</div>
        )}

        {/* ── Wizard ─────────────────────────────────────────────── */}
        <div className="ap-wizard">

          {/* ── Step 0: Make ─────────────────────────────────────── */}
          <div className="ap-step-block">
            <StepHeader stepIndex={0} label="Make" value={form.make} />
            {step === 0 && (
              <div className="ap-options">
                {makes.length === 0
                  ? <p className="ap-no-options">Loading makes…</p>
                  : makes.map(m => (
                    <button
                      key={m.make}
                      className={`ap-option-btn ${form.make === m.make ? 'selected' : ''}`}
                      onClick={() => selectMake(m.make)}
                    >{m.make}</button>
                  ))
                }
              </div>
            )}
          </div>

          {/* ── Step 1: Model ────────────────────────────────────── */}
          {step >= 1 && (
            <div className="ap-step-block">
              <StepHeader stepIndex={1} label="Model" value={form.model} />
              {step === 1 && (
                <div className="ap-options">
                  {models.length === 0
                    ? <p className="ap-no-options">Loading models…</p>
                    : models.map(m => (
                      <button
                        key={m.model}
                        className={`ap-option-btn ${form.model === m.model ? 'selected' : ''}`}
                        onClick={() => selectModel(m.model)}
                      >{m.model}</button>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Year ─────────────────────────────────────── */}
          {step >= 2 && (
            <div className="ap-step-block">
              <StepHeader stepIndex={2} label="Year" value={form.year} />
              {step === 2 && (
                <div className="ap-options">
                  {years.length === 0
                    ? <p className="ap-no-options">Loading years…</p>
                    : years.map(y => (
                      <button
                        key={y.year}
                        className={`ap-option-btn ${String(form.year) === String(y.year) ? 'selected' : ''}`}
                        onClick={() => selectYear(String(y.year))}
                      >{y.year}</button>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Package / Trim ───────────────────────────── */}
          {step >= 3 && (
            <div className="ap-step-block">
              <StepHeader stepIndex={3} label="Package / Trim" value={form.pkg} />
              {step === 3 && (
                <div className="ap-options">
                  {packages.length === 0
                    ? <p className="ap-no-options">Loading packages…</p>
                    : packages.map(p => (
                      <button
                        key={p.trim_package}
                        className={`ap-option-btn ${form.pkg === p.trim_package ? 'selected' : ''}`}
                        onClick={() => selectPackage(p.trim_package)}
                      >{p.trim_package}</button>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Engine ───────────────────────────────────── */}
          {step >= 4 && (
            <div className="ap-step-block">
              <StepHeader stepIndex={4} label="Engine" value={form.engine} />
              {step === 4 && (
                <div className="ap-options">
                  {engines.length === 0
                    ? <p className="ap-no-options">Loading engines…</p>
                    : engines.map(e => (
                      <button
                        key={e.engine_desc}
                        className={`ap-option-btn ${form.engine === e.engine_desc ? 'selected' : ''}`}
                        onClick={() => selectEngine(e.engine_desc)}
                      >{e.engine_desc}</button>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          {/* ── Step 5: Additional details ───────────────────────── */}
          {step >= 5 && (
            <div className="ap-step-block">
              <StepHeader stepIndex={5} label="Details" />
              {step === 5 && (
                <div className="ap-details-form">

                  <div className="ap-field">
                    <label className="ap-label">Project Nickname</label>
                    <input
                      className="ap-input"
                      placeholder='e.g. "The Z28"'
                      value={form.nickname}
                      onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))}
                    />
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">VIN (optional)</label>
                    <input
                      className="ap-input"
                      placeholder="Vehicle identification number"
                      value={form.vin}
                      onChange={e => setForm(f => ({ ...f, vin: e.target.value }))}
                    />
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">Transmission (optional)</label>
                    <input
                      className="ap-input"
                      placeholder='e.g. "Muncie 4-speed M22"'
                      value={form.transmission_desc}
                      onChange={e => setForm(f => ({ ...f, transmission_desc: e.target.value }))}
                    />
                  </div>

                  {error && <p className="ap-error">{error}</p>}

                  <button
                    className="ap-start-btn"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? 'Saving…' : '🏁 Start My Project'}
                  </button>

                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
